import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { CreditCard, DollarSign, Calendar, Clock } from "lucide-react";
import { useCustomer } from "@/contexts/CustomerContext";
import { formatCurrency } from "@/lib/lendingCalculations";

const statusColor: Record<string, string> = {
  active: "bg-success/10 text-success",
  disbursed: "bg-primary/10 text-primary",
  pending: "bg-warning/10 text-warning",
  settled: "bg-muted text-muted-foreground",
  late: "bg-destructive/10 text-destructive",
  paid: "bg-success/10 text-success",
  partial: "bg-warning/10 text-warning",
};

export default function MyLoansPage() {
  const {
    loans,
    loanApplications,
    installments,
    handleLencoPayment,
    handleLencoSettlement,
    lencoPaymentLoading,
    lencoSettlementLoading,
    totalOutstanding,
  } = useCustomer();

  const [customAmounts, setCustomAmounts] = useState<Record<string, string>>({});
  const [selectedLoanId, setSelectedLoanId] = useState<string | null>(
    loans.find((l) => l.status === "active")?.id || loans[0]?.id || null
  );

  // Loans load async — fall back to the active (or first) loan so the detail
  // pane and Repay action are visible without an extra click.
  const selectedLoan =
    loans.find((l) => l.id === selectedLoanId) ||
    loans.find((l) => l.status === "active") ||
    loans[0] ||
    null;
  const loanInstallments = installments.filter((i) => i.loan_id === selectedLoan?.id);
  // Next due is across ALL loans, not just the selected one.
  const nextInstallment = [...installments]
    .filter((i) => i.status !== "paid" && i.due_date)
    .sort((a, b) => a.due_date.localeCompare(b.due_date))[0];
  const activeLoans = loans.filter((l) => l.status === "active");
  const pendingApps = loanApplications.filter((a) => a.status === "pending");

  if (loans.length === 0 && pendingApps.length === 0) {
    return (
      <div className="mx-auto max-w-lg space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">My Loans</h1>
          <p className="text-sm text-muted-foreground">You don't have any loans yet.</p>
        </div>
        <Card className="border border-border/40">
          <CardContent className="flex flex-col items-center gap-4 py-12">
            <CreditCard className="h-12 w-12 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">
              Once you borrow, your loans and repayment schedule will appear here.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">My Loans</h1>
        <p className="text-sm text-muted-foreground">
          Track balances, make payments, and view your repayment schedule.
        </p>
      </div>

      {/* KPIs */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="border border-border/40">
          <CardContent className="flex items-center gap-3 py-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <DollarSign className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total outstanding</p>
              <p className="text-lg font-semibold">{formatCurrency(totalOutstanding, "ZMW")}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-border/40">
          <CardContent className="flex items-center gap-3 py-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-success/10">
              <CreditCard className="h-5 w-5 text-success" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Active loans</p>
              <p className="text-lg font-semibold">{activeLoans.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-border/40">
          <CardContent className="flex items-center gap-3 py-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-warning/10">
              <Calendar className="h-5 w-5 text-warning" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Next due</p>
              <p className="text-lg font-semibold">
                {nextInstallment
                  ? new Date(nextInstallment.due_date).toLocaleDateString()
                  : "—"}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending applications */}
      {pendingApps.length > 0 && (
        <Card className="border-warning/30 bg-warning/5">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Pending applications</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pendingApps.map((app) => (
              <div key={app.id} className="flex items-center justify-between text-sm">
                <span>
                  {formatCurrency(app.amount, "ZMW")} · {app.term_months} months · {app.purpose}
                </span>
                <Badge className="bg-warning/10 text-warning">Pending review</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-[0.4fr_0.6fr]">
        {/* Loan list */}
        <div className="space-y-2">
          <p className="text-sm font-medium text-muted-foreground">Your loans</p>
          {loans.map((loan) => {
            const progress =
              ((loan.principal_amount - loan.outstanding_balance) / loan.principal_amount) * 100;
            return (
              <button
                key={loan.id}
                onClick={() => setSelectedLoanId(loan.id)}
                className={`w-full rounded-xl border p-4 text-left transition-colors ${
                  selectedLoan?.id === loan.id
                    ? "border-primary/30 bg-primary/5"
                    : "border-border/40 hover:border-primary/20"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    {formatCurrency(loan.principal_amount, "ZMW")} · {loan.term_months} months
                  </span>
                  <Badge className={statusColor[loan.status] || "bg-muted text-muted-foreground"}>
                    {loan.status}
                  </Badge>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">Ref {loan.id.slice(-8)}</p>
                <Progress value={Math.min(100, progress)} className="mt-2 h-1.5" />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {
                    installments.filter((i) => i.loan_id === loan.id && i.status === "paid").length
                  } of {loan.term_months} payments made
                </p>
              </button>
            );
          })}
        </div>

        {/* Selected loan detail */}
        {selectedLoan && (
          <div className="space-y-4">
            <Card className="border border-border/40">
              <CardHeader className="pb-3">
                <CardTitle className="text-base">
                  Loan #{selectedLoan.id.slice(-8)}
                </CardTitle>
                <CardDescription>
                  {selectedLoan.loan_type || "Personal loan"} ·{" "}
                  {selectedLoan.interest_rate}% p.a.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-muted-foreground">Principal</p>
                    <p className="font-semibold">
                      {formatCurrency(selectedLoan.principal_amount, "ZMW")}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Outstanding</p>
                    <p className="font-semibold">
                      {formatCurrency(selectedLoan.outstanding_balance, "ZMW")}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Monthly payment</p>
                    <p className="font-semibold">
                      {formatCurrency(selectedLoan.monthly_payment, "ZMW")}
                    </p>
                  </div>
                  <div>
                    <p className="text-muted-foreground">Due date</p>
                    <p className="font-semibold">
                      {new Date(selectedLoan.due_date).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                {selectedLoan.status === "active" && (
                  <div className="flex flex-wrap gap-2 pt-2">
                    <Button
                      size="sm"
                      disabled={lencoPaymentLoading === selectedLoan.id}
                      onClick={() =>
                        handleLencoPayment(
                          selectedLoan,
                          Number(selectedLoan.monthly_payment),
                          "Installment"
                        )
                      }
                    >
                      {lencoPaymentLoading === selectedLoan.id
                        ? "Processing..."
                        : "Pay installment"}
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={lencoSettlementLoading === selectedLoan.id}
                      onClick={() => handleLencoSettlement(selectedLoan)}
                    >
                      {lencoSettlementLoading === selectedLoan.id
                        ? "Preparing..."
                        : "Settle full balance"}
                    </Button>
                  </div>
                )}

                {selectedLoan.status === "active" && (
                  <div className="pt-2 border-t border-border/40">
                    <Label className="text-xs text-muted-foreground">
                      Custom amount
                    </Label>
                    <div className="mt-1 flex gap-2">
                      <Input
                        value={customAmounts[selectedLoan.id] || ""}
                        onChange={(e) =>
                          setCustomAmounts((prev) => ({
                            ...prev,
                            [selectedLoan.id]: e.target.value,
                          }))
                        }
                        placeholder="e.g. 250"
                        type="number"
                        min="1"
                        className="h-8 text-xs"
                      />
                      <Button
                        size="sm"
                        className="text-xs"
                        disabled={
                          !customAmounts[selectedLoan.id] ||
                          Number(customAmounts[selectedLoan.id]) <= 0 ||
                          lencoPaymentLoading === selectedLoan.id
                        }
                        onClick={() =>
                          handleLencoPayment(
                            selectedLoan,
                            Number(customAmounts[selectedLoan.id]),
                            "Custom payment"
                          )
                        }
                      >
                        Pay
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Repayment schedule */}
            {loanInstallments.length > 0 && (
              <Card className="border border-border/40">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Repayment schedule</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {loanInstallments.slice(0, 12).map((inst) => {
                    const dueDate = new Date(inst.due_date);
                    const isLate = inst.status !== "paid" && dueDate < new Date();
                    return (
                      <div
                        key={inst.id}
                        className="flex items-center justify-between rounded-lg border border-border/30 p-3 text-sm"
                      >
                        <div>
                          <p className="font-medium">
                            Due {dueDate.toLocaleDateString()}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {formatCurrency(inst.amount_due, "ZMW")} · Paid{" "}
                            {formatCurrency(inst.amount_paid, "ZMW")}
                          </p>
                        </div>
                        <Badge
                          className={
                            isLate
                              ? "bg-destructive/10 text-destructive"
                              : statusColor[inst.status] ||
                                "bg-muted text-muted-foreground"
                          }
                        >
                          {isLate ? "late" : inst.status}
                        </Badge>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
